import React, { useCallback, useMemo, useState } from 'react';
import { CreditCardData } from 'data/shared/CreditCardData';
import {
  CardData,
  SignupBonus,
} from 'data/types/CardData';
import { TableCardButton } from 'components/TableCardButton';
import { PageWrapper } from 'components/design-system/PageWrapper';
import { Navigation } from 'components/Navigation';
import { CardContainer } from 'components/CardContainer';
import { CreditCardRewardsType } from 'data/shared/CreditCardRewardsTypes';
import { usePageMeta } from '../hooks/usePageMeta';

interface SignupBonusWithCard {
  card: CardData;
  signupBonus: SignupBonus;
  netValue: number;
  signupBonusValue: number;
  annualFee: number;
  creditsValue: number;
}

// Helper function to get default point value based on rewards type
const getDefaultPointValue = (rewardsType: string): number => {
  // Conservative default values (in cents per point/mile)
  const defaultValues: Record<string, number> = {
    // Cash back
    [CreditCardRewardsType.CASH_BACK]: 1.0,
    [CreditCardRewardsType.GIFT_CARD]: 1.0,

    // Transferable points
    [CreditCardRewardsType.CHASE_ULTIMATE_REWARDS]: 1.5, // Conservative since we don't know if user has Sapphire cards
    [CreditCardRewardsType.AMERICAN_EXPRESS_MEMBERSHIP_REWARDS]: 2.0,
    [CreditCardRewardsType.CITI_THANKYOU_POINTS]: 1.8,
    [CreditCardRewardsType.CAPITAL_ONE_REWARDS]: 1.85,
    [CreditCardRewardsType.BILT_REWARDS]: 2.05,

    // Bank points
    [CreditCardRewardsType.WELLS_FARGO_REWARDS]: 1.6,
    [CreditCardRewardsType.BANK_OF_AMERICA_REWARDS]: 1.0,
    [CreditCardRewardsType.BARCLAYCARD_REWARDS]: 1.0,
    [CreditCardRewardsType.RCI_REWARDS]: 1.0,
    [CreditCardRewardsType.BREX]: 1.0,
    [CreditCardRewardsType.US_BANK_REWARDS]: 1.5,

    // Hotel points
    [CreditCardRewardsType.MARRIOTT_BONVOY]: 0.85,
    [CreditCardRewardsType.HILTON]: 0.6,
    [CreditCardRewardsType.HYATT]: 1.7,
    [CreditCardRewardsType.IHG]: 0.5,
    [CreditCardRewardsType.WYNDHAM]: 1.1,
    [CreditCardRewardsType.CHOICE]: 0.6,

    // Airline miles - major US airlines
    [CreditCardRewardsType.UNITED]: 1.35,
    [CreditCardRewardsType.AMERICAN]: 1.6,
    [CreditCardRewardsType.DELTA]: 1.5,
    [CreditCardRewardsType.SOUTHWEST]: 1.4,
    [CreditCardRewardsType.ALASKA]: 1.4,
    [CreditCardRewardsType.JETBLUE]: 1.3,
    [CreditCardRewardsType.HAWAIIAN]: 1.2,
    [CreditCardRewardsType.FRONTIER]: 1.1,
    [CreditCardRewardsType.SPIRIT_AIRLINES]: 1.0,

    // International airline miles
    [CreditCardRewardsType.AVIOS]: 1.5,
    [CreditCardRewardsType.AIR_FRANCE_KLM_FLYING_BLUE]: 1.3,
    [CreditCardRewardsType.AIR_CANADA_AEROPLAN]: 1.5,
    [CreditCardRewardsType.LUFTHANSA]: 1.3,
    [CreditCardRewardsType.EMIRATES]: 1.4,

    // Cruise points
    [CreditCardRewardsType.CARNIVAL]: 0.8,
    [CreditCardRewardsType.NORWEGIAN_CRUISE_POINTS]: 0.8,
    [CreditCardRewardsType.MYCRUISE_POINTS]: 0.8,

    // Other
    [CreditCardRewardsType.DISNEY_REWARDS_DOLLARS]: 1.0,
    [CreditCardRewardsType.BREEZEPOINTS]: 1.0,
    [CreditCardRewardsType.ALLWAYS_POINTS]: 1.1,
    [CreditCardRewardsType.EXPEDIA]: 1.0,
    [CreditCardRewardsType.GM_REWARDS_POINTS]: 1.0,
    [CreditCardRewardsType.GM_BUSINESS_CARD_POINTS]: 1.0,
    [CreditCardRewardsType.GAME_PASS_ULTIMATE]: 1.0,
  };

  return defaultValues[rewardsType] || 1.0;
};

const SignupBonuses = () => {
  usePageMeta({
    title: 'Best Credit Card Signup Bonuses - CardClues',
    description:
      'Compare the best credit card signup bonuses ranked by value. Find welcome offers worth $500+ and see which cards have the highest net value after annual fees.',
    canonicalPath: '/signup-bonuses',
  });

  // Card type filter state
  const [cardTypeFilter, setCardTypeFilter] = useState<
    'personal' | 'business' | 'all'
  >('personal');

  // Get all cards data
  const allCards = useMemo(() => Object.values(CreditCardData), []);

  // Calculate signup bonus values and net value for ranking
  const signupBonusesWithCards = useMemo(() => {
    const bonuses: SignupBonusWithCard[] = [];

    allCards.forEach((card) => {
      if (!card.signupBonus || card.signupBonus.length === 0) {
        return;
      }

      // Apply card type filter
      if (cardTypeFilter !== 'all') {
        if (cardTypeFilter === 'personal' && card.cardType === 'business') {
          return;
        }
        if (cardTypeFilter === 'business' && card.cardType !== 'business') {
          return;
        }
      }

      card.signupBonus.forEach((bonus) => {
        // Calculate signup bonus value
        let signupBonusValue = 0;

        // Get the card's rewards type to determine proper point valuation
        const rewardsType = card.rewardsType;
        const pointsValue = getDefaultPointValue(rewardsType);

        // Handle main rewards
        if (bonus.rewards) {
          signupBonusValue += bonus.rewards.reduce((sum, reward) => {
            if (reward.amount && reward.value_unit === 'dollars') {
              return sum + reward.amount;
            }
            // For points/miles, use proper valuation based on card's rewards type
            if (
              reward.amount &&
              (reward.value_unit === 'points' || reward.value_unit === 'miles')
            ) {
              return sum + reward.amount * (pointsValue / 100);
            }
            return sum;
          }, 0);
        }

        // Handle tiers
        if (bonus.tiers) {
          signupBonusValue += bonus.tiers.reduce((sum, tier) => {
            return (
              sum +
              tier.rewards.reduce((tierSum, reward) => {
                if (reward.amount && reward.value_unit === 'dollars') {
                  return tierSum + reward.amount;
                }
                // For points/miles, use proper valuation based on card's rewards type
                if (
                  reward.amount &&
                  (reward.value_unit === 'points' ||
                    reward.value_unit === 'miles')
                ) {
                  return tierSum + reward.amount * (pointsValue / 100);
                }
                return tierSum;
              }, 0)
            );
          }, 0);
        }

        // Get annual fee
        const annualFee =
          card.annualFee || card.fees?.annual_fee_amount_dollars || 0;

        // Calculate credits value (only first year credits, up to annual fee)
        let creditsValue = 0;
        if (card.credits) {
          const firstYearCredits = card.credits.filter(
            (credit) => credit.applies_first_year
          );
          creditsValue = firstYearCredits.reduce((sum, credit) => {
            if (credit.value && credit.value_unit === 'dollars') {
              return sum + credit.value;
            }
            return sum;
          }, 0);

          // Cap credits value at annual fee
          creditsValue = Math.min(creditsValue, annualFee);
        }

        // Calculate net value: signup bonus - annual fee + credits (capped at annual fee)
        const netValue = signupBonusValue - annualFee + creditsValue;

        bonuses.push({
          card,
          signupBonus: bonus,
          netValue,
          signupBonusValue,
          annualFee,
          creditsValue,
        });
      });
    });

    // Sort by net value descending
    return bonuses.sort((a, b) => b.netValue - a.netValue);
  }, [allCards, cardTypeFilter]);

  const formatSignupBonusDescription = (bonus: SignupBonus) => {
    let description = bonus.description;

    if (bonus.spendRequirement) {
      const months = bonus.spendRequirementMonths || 3;
      description += ` (Spend $${bonus.spendRequirement.toLocaleString()} in ${months} months)`;
    }

    return description;
  };

  const handleCardClick = useCallback((card: CardData) => {
    const url = card.learn_more_link || card.apply_link;
    if (!url) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const formatValue = (value: number) => {
    if (value === 0) {
      return '$0';
    }
    return value > 0
      ? `+$${value.toFixed(0)}`
      : `-$${Math.abs(value).toFixed(0)}`;
  };

  return (
    <PageWrapper showFooter={false}>
      <Navigation />
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <h1
          style={{
            fontFamily: 'Merriweather',
            fontSize: '32px',
            marginBottom: '20px',
            textAlign: 'center',
          }}
        >
          Best Sign-Up Bonuses
        </h1>

        <p
          style={{
            textAlign: 'center',
            marginBottom: '20px',
            color: '#666',
            fontSize: '16px',
          }}
        >
          Ranked by net value: signup bonus - annual fee + first-year credits
          (capped at annual fee)
        </p>

        {/* Card Type Filter */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '30px',
          }}
        >
          <button
            onClick={() => setCardTypeFilter('personal')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor:
                cardTypeFilter === 'personal' ? '#4DA9E6' : '#f0f0f0',
              color: cardTypeFilter === 'personal' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Personal Cards
          </button>
          <button
            onClick={() => setCardTypeFilter('business')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor:
                cardTypeFilter === 'business' ? '#4DA9E6' : '#f0f0f0',
              color: cardTypeFilter === 'business' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Business Cards
          </button>
          <button
            onClick={() => setCardTypeFilter('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: cardTypeFilter === 'all' ? '#4DA9E6' : '#f0f0f0',
              color: cardTypeFilter === 'all' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            All Cards
          </button>
        </div>

        {/* Cards List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {signupBonusesWithCards.map((item, index) => (
            <CardContainer key={`${item.card.id}-${index}`}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}
              >
                {/* Card Image */}
                <div style={{ flexShrink: 0 }}>
                  <TableCardButton
                    cardData={item.card}
                    onClick={() => handleCardClick(item.card)}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  />
                </div>

                {/* Card Details */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: '18px',
                          fontFamily: 'Merriweather',
                          fontWeight: 'bold',
                        }}
                      >
                        {item.card.name}
                      </h3>
                      {item.card.cardType && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            textTransform: 'capitalize',
                            backgroundColor: '#f0f0f0',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginLeft: '8px',
                          }}
                        >
                          {item.card.cardType}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color:
                          item.netValue > 0
                            ? '#28a745'
                            : item.netValue < 0
                            ? '#dc3545'
                            : '#666',
                      }}
                    >
                      {formatValue(item.netValue)}
                    </div>
                  </div>

                  {/* Sign-up Bonus Description */}
                  <div style={{ marginBottom: '12px' }}>
                    <p
                      style={{ margin: 0, fontSize: '14px', lineHeight: '1.4' }}
                    >
                      {formatSignupBonusDescription(item.signupBonus)}
                    </p>
                  </div>

                  {/* Value Breakdown */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '16px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        Sign-up Value:
                      </span>
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: item.signupBonusValue > 0 ? '#28a745' : '#666',
                        }}
                      >
                        {formatValue(item.signupBonusValue)}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        Annual Fee:
                      </span>
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: item.annualFee > 0 ? '#dc3545' : '#666',
                        }}
                      >
                        {formatValue(-item.annualFee)}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        First-Year Credits:
                      </span>
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: item.creditsValue > 0 ? '#28a745' : '#666',
                        }}
                      >
                        {formatValue(item.creditsValue)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContainer>
          ))}
        </div>

        {signupBonusesWithCards.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666',
            }}
          >
            No sign-up bonuses found in the current dataset.
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default SignupBonuses;
